import {
  capitalize,
  cutString,
  formatStringToDateFormat,
  optimizeImageCloudinary,
  removeSpecialCharacter,
} from "lib/helpers"
import { useContext, useEffect, useMemo, useRef, useState } from "react"

import { gtmEvent } from "lib/gtm"
import Breadcrumbs from "lib/components/Breadcrumbs"
import Icons from "lib/components/Icons"
import ImageComponent from "lib/components/Image"
import Text from "lib/components/Text"
import GhostContentAPI from "@tryghost/content-api"
import { LanguageContext } from "context/LanguageContext"
import sha256 from "crypto-js/sha256"
import { NextSeo } from "next-seo"
import { useRouter } from "next/router"
import url from "url"
import NotFoundTitle from "lib/components/NotFoundTitle"
import useSWR from "swr"
import fetcher from "lib/fetcher"
import ProgramList from "@components/List/ProgramList"
import { initializeApollo } from "lib/apollo"
import { GetRelatedBlogProgramVariables, RELATED_BLOG_PROGRAM } from "@graphql/blog/related_blog"
import Avatar from "lib/components/Avatar"
import Icon from "lib/components/Icons"
import Slider from "react-slick"
import CardRelatedBlog from "@components/Card/CardPrograms/blogRelated"
import { isMobile } from "react-device-detect"
import { Tooltip } from "react-tooltip"
import { toast } from "react-toastify"
import Button from "lib/components/Button"
import Link from "next/link"
import { PopulatedPosts } from "."

function RelatedProgramBlog({
  blogRelatedProgram,
  loadingNewestProgramPublic,
  title,
  hoveredSlider,
  t,
}) {
  const sliderRef = useRef(null)
  const [checkIsMobile, setCheckIsMobile] = useState(false)

  useEffect(() => {
    setCheckIsMobile(isMobile)
  }, [])

  const settings = {
    className: "center",
    infinite: true,
    slidesToShow: checkIsMobile ? 1.5 : 5,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  }

  if (blogRelatedProgram.length > 0) {
    const dataProgram = blogRelatedProgram.map((item) => ({
      category_slug: item.program.category.slug,
      discount:
        item.program.classes.length > 0
          ? item.program.classes[0].class_detail.discount
          : item.program.program_detail.prices[0].discount,
      discount_price:
        item.program.classes.length > 0
          ? item.program.classes[0].class_detail.discount_price
          : item.program.program_detail.prices[0].discount_price,
      final_rating: 0,
      final_rating_participants: 0,
      price: item.program.program_detail.prices[0].price,
      program_name: item.program.name,
      program_thumbnail_url: item.program.program_thumbnail_url,
      id: item.program.id,
      slug: item.program.slug,
      tp_name: item.program.provider_detail.name,
      tp_profile_url: item.program.provider_detail.profile_url,
      type: item.program.type,
      class_code: item.program.classes.length > 0 ? item.program.classes[0].class_code : null,
      class_end_date:
        item.program.classes.length > 0 ? item.program.classes[0].class_detail.start_date : null,
      class_start_date:
        item.program.classes.length > 0 ? item.program.classes[0].class_detail.end_date : null,
    }))

    const nextSlide = () => {
      if (sliderRef.current) {
        sliderRef.current.slickNext()
      }
    }

    const prevSlide = () => {
      if (sliderRef.current) {
        sliderRef.current.slickPrev()
      }
    }

    return (
      <div className="relative container mx-auto">
        <div className="my-8 mx-4 lg:mx-0 sm:mt-8">
          <div className="my-4 flex flex-row items-center justify-between">
            <Text size="extra-large" weight="semibold" color="gray-darkest" customClass="leading-8">
              {title}
            </Text>
            <Link href="/programs/all" className="hover:underline">
              <Text size="small" weight="semibold" color="primary" customClass="leading-8">
                {t("view_all")}
              </Text>
            </Link>
          </div>
          {dataProgram.length > 5 ? (
            <div className="slider-container">
              <Slider ref={sliderRef} {...settings}>
                {dataProgram.map((item, index) => (
                  <CardRelatedBlog
                    key={index}
                    index={index}
                    sectionName="Blog Related Program"
                    category={{
                      name: item.category_name,
                      slug: item.category_slug,
                      description: capitalize(item.category_description),
                    }}
                    discount={item.discount}
                    discountPrice={item.discount_price}
                    finalRating={item.final_rating}
                    finalRatingParticipants={item.final_rating_participants}
                    price={item.price}
                    programName={item.program_name}
                    programThumbnailUrl={item.program_thumbnail_url}
                    id={item.id}
                    slug={item.slug}
                    tpName={item.tp_name}
                    tpProfileUrl={item.tp_profile_url}
                    type={item.type}
                    classCode={item.class_code}
                    classEndDate={item.class_end_date}
                    classStartDate={item.class_start_date}
                    customImage={item.custom_image}
                    campaign={item.campaign}
                  />
                ))}
              </Slider>
              {hoveredSlider && (
                <div className="absolute top-1/2 transform -translate-y-1/2 left-[-35px] lg:left-[-50px]">
                  <Icon
                    iconName="arrowLeft"
                    styles="bg-[#F8F8F8] fill-black hover:bg-black hover:fill-white rounded-full p-1 lg:p-2 cursor-pointer prev"
                    width={isMobile ? 26 : 40}
                    height={isMobile ? 26 : 40}
                    onClick={prevSlide}
                  />
                </div>
              )}
              {hoveredSlider && (
                <div className="absolute top-1/2 transform -translate-y-1/2 right-[-35px] lg:right-[-50px]">
                  <Icon
                    iconName="arrow-right"
                    styles="bg-[#F8F8F8] fill-black hover:bg-black hover:fill-white rounded-full p-1 lg:p-2 cursor-pointer next"
                    width={isMobile ? 26 : 40}
                    height={isMobile ? 26 : 40}
                    onClick={nextSlide}
                  />
                </div>
              )}
            </div>
          ) : (
            <ProgramList
              sectionName="Blog Related Program"
              programs={dataProgram}
              loading={loadingNewestProgramPublic}
            />
          )}
        </div>
      </div>
    )
  }
  return <div />
}

const apolloClient = initializeApollo()

type TagsQuery = {
  name: string
  slug: string
}

type Tags = {
  tags: Array<TagsQuery>
}

interface BlogDetailQuery {
  id: string
  title: string
  html: string
  feature_image: string
  feature_image_alt: string
  primary_tag: {
    name: string
    slug: string
  }
  tags: {
    name: string
    slug: string
  }[]
  primary_author: {
    name: string
    profile_image: string
  }
  slug: string
  published_at: string
  created_at: string
  excerpt: string
}

interface BlogDetailProps {
  blogDetailPost: BlogDetailQuery | null
  error?: string
}

interface BlogDetailDataProps {
  blogDetailPost: BlogDetailQuery | null
  recomendationPost: any
  blogRelatedProgram?: {
    blog_related_programs: {
      program: {
        id: string
      }
    }[]
  }
}

const api = new GhostContentAPI({
  url: process.env.NEXT_PUBLIC_GHOST_HOST,
  key: process.env.NEXT_PUBLIC_GHOST_KEY,
  version: "v3",
})

export async function getServerSideProps(ctx) {
  const { slug } = ctx.params

  try {
    const posts: BlogDetailProps = await api.posts
      .read({ slug }, { include: "tags,authors" })
      .then((post) => {
        const populateQueryPosts: BlogDetailProps = {
          blogDetailPost: {
            id: post.id,
            title: post.title,
            html: post.html,
            feature_image: post.feature_image,
            feature_image_alt: post.feature_image_alt,
            primary_author: {
              name: post.primary_author.name,
              profile_image: post.primary_author.profile_image,
            },
            primary_tag: {
              name: post.primary_tag.name,
              slug: post.primary_tag.slug,
            },
            tags: post.tags,
            published_at: post.published_at,
            slug: post.slug,
            created_at: post.created_at,
            excerpt: post.excerpt,
          },
        }
        return {
          blogDetailPost: populateQueryPosts.blogDetailPost,
        }
      })
      .catch(() => ({
        blogDetailPost: null,
        error: "Post not found!",
      }))

    const recomendation = await api.posts
      .browse({
        include: "tags,reading_time,authors",
        limit: 3,
        filter: `tag:${posts.blogDetailPost.primary_tag.slug}+id:-${posts.blogDetailPost.id}`,
      })
      .then(async (recomendationPosts) => {
        const populatedPosts: PopulatedPosts = {
          posts: recomendationPosts,
          meta: recomendationPosts.meta,
        }
        return {
          recomendationPost: populatedPosts,
        }
      })
      .catch((err) => ({
        recomendationPost: null,
        error: "Post not found!",
      }))

    if (posts.error) {
      return {
        props: {
          blogDetailPost: null,
        },
      }
    }

    if (recomendation.error) {
      return {
        props: {
          recomendationPost: null,
        },
      }
    }

    const { data: blogRelatedProgram } = await apolloClient.query<GetRelatedBlogProgramVariables>({
      variables: {
        slug,
      },
      query: RELATED_BLOG_PROGRAM,
      fetchPolicy: "no-cache",
    })

    return {
      props: {
        blogDetailPost: posts.blogDetailPost,
        recomendationPost: recomendation,
        blogRelatedProgram,
      },
    }
  } catch (error) {
    return {
      props: {
        blogDetailPost: null,
        error: "Post not found!",
      },
    }
  }
}

function FeaturedBlog({
  blogDetailPost,
  blogRelatedProgram,
  recomendationPost,
}: BlogDetailDataProps) {
  const router = useRouter()
  const { useTranslation: t } = useContext(LanguageContext)
  const [hoveredSlider, setHoveredSlider] = useState<boolean>(false)
  const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false)
  const { pathname } = url.parse(router.asPath)
  const { data: newestProgramPublic, isLoading: loadingNewestProgramPublic } = useSWR(
    "/api/blog/new_public",
    fetcher,
  )

  const sliderRef = useRef(null)
  const [checkIsMobile, setCheckIsMobile] = useState(false)

  useEffect(() => {
    setCheckIsMobile(isMobile)
  }, [])

  const settings = {
    className: "center",
    infinite: true,
    slidesToShow: checkIsMobile ? 1.5 : 5,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  }

  useEffect(() => {
    if (blogDetailPost) {
      gtmEvent("ce_article_detail", {
        article_id: blogDetailPost.id,
        article_title: removeSpecialCharacter(blogDetailPost.title),
        article_author: sha256(blogDetailPost.primary_author.name).toString(),
        article_published_date: formatStringToDateFormat(blogDetailPost.published_at, "YYYY-MM-DD"),
        article_category: blogDetailPost.primary_tag.name,
        article_posted_by: blogDetailPost.primary_author.name,
      })
    }
  }, [])

  if (!blogDetailPost) {
    return <NotFoundTitle title={t("post_not_found")} />
  }

  const nextSlide = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext()
    }
  }

  const prevSlide = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev()
    }
  }

  const renderTagTextColor = (value: string) => {
    switch (value) {
      case "Insight":
        return "!text-secondary"
      case "Trainer":
        return "!text-warning"
      case "Activity":
        return "!text-danger"
      case "Case Study":
        return "!text-primary"
      default:
        return "!text-black"
    }
  }

  const TagsComponent: React.FC<Tags> = ({ tags }) => (
    <div className="flex flex-wrap space-x-1">
      {tags.map((tag, index) => (
        <Text key={index} customClass={`text-base font-medium ${renderTagTextColor(tag.name)}`}>
          {tag.name}
          {index < tags.length - 1 && <span className="ml-1">|</span>}
        </Text>
      ))}
    </div>
  )

  return (
    <>
      <NextSeo
        title={`${blogDetailPost.title} | PasarTrainer`}
        description={blogDetailPost.excerpt}
        canonical={`${process.env.NEXT_PUBLIC_URL}${pathname}`}
        openGraph={{
          type: "website",
          locale: "en_ID",
          url: `${process.env.NEXT_PUBLIC_URL}${pathname}`,
          title: blogDetailPost.title,
          site_name: blogDetailPost.title,
          description: blogDetailPost.excerpt,
          images: [
            {
              url: blogDetailPost.feature_image || "/assets/images/program-image.webp",
              alt: `${blogDetailPost.feature_image_alt} - PasarTrainer`,
              width: 800,
              height: 600,
            },
          ],
        }}
      />

      <div className="flex flex-col">
        <div className="container mx-auto p-4 lg:py-4 lg:px-0 !text-sm md:!text-base">
          <Breadcrumbs
            size="small"
            color="gray"
            format="slash"
            breadcrumbsData={[
              { label: "Blog", href: "/blog" },
              { label: `${cutString(blogDetailPost.title, 50)}`, href: "/", color: "gray" },
            ]}
          />
        </div>
        <div className="bg-[#F8F8F8]">
          <div className="container mx-auto px-4 md:px-0 py-10 md:py-16">
            <div className="flex flex-col items-start gap-y-4">
              <TagsComponent tags={blogDetailPost.tags} />
              <h1 className="font-bold text-lg md:text-4xl text-start">{blogDetailPost.title}</h1>
            </div>
          </div>
        </div>
        <div className="p-4 lg:p-0 container mx-auto grid grid-cols-1 md:grid-cols-12 gap-x-8 mt-0 lg:mt-10">
          <div className="cols-span-12 md:col-span-8 flex flex-col">
            <div className="flex flex-row justify-between items-center border-b py-2">
              <div className="flex flex-row items-center">
                <Avatar
                  rounded="full"
                  size="small"
                  variant="circular-image"
                  url={
                    blogDetailPost.primary_author.profile_image
                      ? blogDetailPost.primary_author.profile_image
                      : optimizeImageCloudinary(
                          "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1669214024/client/no-image.webp",
                        )
                  }
                />
                <div className="ml-2">
                  <Text size="small" alignText="left" weight="normal">
                    {blogDetailPost.primary_author.name}
                  </Text>
                  <Text
                    customClass="!text-[#959EAD]"
                    size="extra-small"
                    alignText="left"
                    weight="normal">
                    {formatStringToDateFormat(blogDetailPost.published_at, "DD MMM YYYY")}
                  </Text>
                </div>
              </div>
              <div id="click-share">
                <Tooltip
                  isOpen={isOpenTooltip}
                  clickable
                  anchorSelect="#click-share"
                  place="bottom"
                  className="!p-0 !bg-white border shadow z-10 !opacity-100 rounded">
                  <div className="flex flex-col bg-white">
                    <div
                      role="button"
                      tabIndex={0}
                      className="flex flex-row items-center gap-x-2  p-2"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(
                            `${`${process.env.NEXT_PUBLIC_CLIENT_URL}/${blogDetailPost.slug}`}`,
                          )
                          toast.success("Success Copy")
                        } catch (error) {
                          toast.error("Failed Copy")
                        }
                      }}>
                      <Icons iconName="link" width={16} height={16} color="#6B7280" />
                      <Text size="extra-small" weight="normal" color="default">
                        Copy Link
                      </Text>
                    </div>
                    <hr />
                    <div className="flex p-2">
                      <a
                        target="_blank"
                        href={`https://twitter.com/intent/tweet?text=${
                          blogDetailPost.title
                        }&url=${`${process.env.NEXT_PUBLIC_CLIENT_URL}/blog/${blogDetailPost.slug}`}`}
                        rel="noreferrer">
                        <div
                          role="button"
                          tabIndex={0}
                          className="flex flex-row items-center gap-x-2"
                          onClick={() =>
                            gtmEvent("ce_article_share", {
                              article_id: blogDetailPost.id,
                              article_title: removeSpecialCharacter(blogDetailPost.title),
                              article_author: sha256(blogDetailPost.primary_author.name).toString(),
                              article_published_date: formatStringToDateFormat(
                                blogDetailPost.published_at,
                                "YYYY-MM-DD",
                              ),
                              article_category: blogDetailPost.primary_tag.name,
                              article_posted_by: blogDetailPost.primary_author.name,
                              share_method: "Twitter",
                            })
                          }>
                          <Icons iconName="twitter" width={16} height={16} color="#6B7280" />
                          <Text size="extra-small" weight="normal" color="default">
                            Share on Twitter
                          </Text>
                        </div>
                      </a>
                    </div>
                    <div className="flex p-2">
                      <a
                        target="_blank"
                        href={`https://www.facebook.com/sharer/sharer.php?u=${`${process.env.NEXT_PUBLIC_CLIENT_URL}/blog/${blogDetailPost.slug}`}`}
                        rel="noreferrer">
                        <div
                          role="button"
                          tabIndex={0}
                          className="flex flex-row items-center gap-x-2"
                          onClick={() =>
                            gtmEvent("ce_article_share", {
                              article_id: blogDetailPost.id,
                              article_title: removeSpecialCharacter(blogDetailPost.title),
                              article_author: sha256(blogDetailPost.primary_author.name).toString(),
                              article_published_date: formatStringToDateFormat(
                                blogDetailPost.published_at,
                                "YYYY-MM-DD",
                              ),
                              article_category: blogDetailPost.primary_tag.name,
                              article_posted_by: blogDetailPost.primary_author.name,
                              share_method: "Facebook",
                            })
                          }>
                          <Icons iconName="facebook" width={16} height={16} color="#6B7280" />
                          <Text size="extra-small" weight="normal" color="default">
                            Share on Facebook
                          </Text>
                        </div>
                      </a>
                    </div>
                  </div>
                </Tooltip>
                <Icon
                  iconName="share"
                  width={18}
                  height={18}
                  color="#959EAD"
                  onClick={() => setIsOpenTooltip(!isOpenTooltip)}
                />
              </div>
            </div>
            <div className="relative w-full h-96 rounded-lg mt-3">
              <ImageComponent
                objectFit="cover"
                src={
                  blogDetailPost.feature_image
                    ? blogDetailPost.feature_image
                    : optimizeImageCloudinary(
                        "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1669214024/client/no-image.webp",
                      )
                }
                alt={blogDetailPost.feature_image_alt}
                rounded
                roundedSize="large"
              />
            </div>
            <div
              className="mt-5 post-content px-2 md:px-0"
              dangerouslySetInnerHTML={{
                __html: blogDetailPost.html,
              }}
            />
            <div
              role="button"
              tabIndex={0}
              className="mt-8 mb-2"
              onClick={() => {
                gtmEvent("ce_section_engagement", {
                  section_name: t("programs_inhouse"),
                  button_name: t("free_consultation"),
                })
              }}>
              <Button
                variant="contained"
                color="primary"
                href="/business#form_leads_section"
                customClass="w-40 h-10 text-sm sm:w-56 sm:h-12 sm:text-base">
                {t("free_consultation")}
              </Button>
            </div>
          </div>
          <div className="hidden lg:block md:col-span-4 sticky top-28 self-start">
            <div className="flex flex-col">
              <Text weight="semibold" customClass="!text-lg border-b border-black">
                {t("post_recommendation")}
              </Text>
              {recomendationPost.recomendationPost.posts.length > 0 &&
                recomendationPost.recomendationPost.posts.map((blog, index) => (
                  <a href={`/blog/${blog.slug}`} key={index}>
                    <div className="flex flex-col gap-y-1 mt-3 py-1">
                      <Text weight="semibold">{blog.title}</Text>
                      <Text size="small" weight="normal" customClass="">
                        {cutString(blog.excerpt, 150)}
                      </Text>
                      <div
                        className={`flex flex-row items-center justify-between ${
                          index !== 2 ? "border-b border-black" : "border-none"
                        } py-2`}>
                        <Avatar
                          rounded="full"
                          size="extra-small"
                          variant="circular-image"
                          url={
                            blog.primary_author.profile_image
                              ? blog.primary_author.profile_image
                              : optimizeImageCloudinary(
                                  "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1669214024/client/no-image.webp",
                                )
                          }
                        />
                        <Text size="extra-small" weight="normal">
                          {blog.primary_author.name}
                        </Text>
                        <Text size="extra-small" weight="normal" customClass="!text-[#959EAD]">
                          • {formatStringToDateFormat(blog.published_at, "DD MMM YYYY")}
                        </Text>
                        <Text size="extra-small" weight="normal" customClass="!text-[#959EAD]">
                          • {blog.primary_tag.name}
                        </Text>
                      </div>
                    </div>
                  </a>
                ))}
            </div>
            <div
              role="button"
              tabIndex={0}
              className="mt-8 mb-2 w-full"
              onClick={() => {
                gtmEvent("ce_section_engagement", {
                  section_name: t("programs_inhouse"),
                  button_name: t("free_consultation"),
                })
              }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                href="/business#form_leads_section"
                customClass="w-full h-10 text-sm sm:h-12 sm:text-base">
                {t("try_free_consultation_now")}
              </Button>
            </div>
          </div>
        </div>
        <hr className="container mx-auto my-5 mt-10" />
        <div
          onMouseEnter={() => setHoveredSlider(true)}
          onMouseLeave={() => setHoveredSlider(false)}>
          <RelatedProgramBlog
            blogRelatedProgram={blogRelatedProgram.blog_related_programs}
            loadingNewestProgramPublic={loadingNewestProgramPublic}
            title={t("blog_related_program")}
            hoveredSlider={hoveredSlider}
            t={t}
          />
        </div>

        {blogRelatedProgram.blog_related_programs.length <= 0 &&
          newestProgramPublic?.data?.programs.length > 0 && (
            <div className="relative container mx-auto">
              <div className="my-8 mx-4 lg:mx-0 sm:mt-8">
                <div className="my-4 flex flex-row items-center justify-between">
                  <Text
                    size="extra-large"
                    weight="semibold"
                    color="gray-darkest"
                    customClass="leading-8">
                    {t("new_public_class_programs")}
                  </Text>
                  <Link href="/public-class/all" className="hover:underline">
                    <Text size="small" weight="semibold" color="primary" customClass="leading-8">
                      {t("view_all")}
                    </Text>
                  </Link>
                </div>
                {newestProgramPublic?.data?.programs.length > 5 ? (
                  <div className="slider-container">
                    <Slider ref={sliderRef} {...settings}>
                      {newestProgramPublic?.data?.programs.map((item, index) => (
                        <CardRelatedBlog
                          key={index}
                          index={index}
                          sectionName="Blog Related Program"
                          category={{
                            name: item.category_name,
                            slug: item.slug,
                            description: capitalize(item.category_slug),
                          }}
                          discount={item.discount}
                          discountPrice={item.discount_price}
                          finalRating={item.final_rating}
                          finalRatingParticipants={item.final_rating_participants}
                          price={item.price}
                          programName={item.program_name}
                          programThumbnailUrl={item.program_thumbnail_url}
                          id={item.id}
                          slug={item.slug}
                          tpName={item.tp_name}
                          tpProfileUrl={item.tp_profile_url}
                          type={item.type}
                          classCode={item.class_code}
                          classEndDate={item.class_end_date}
                          classStartDate={item.class_start_date}
                          customImage={item.custom_image}
                          campaign={item.campaign}
                        />
                      ))}
                    </Slider>
                    {hoveredSlider && (
                      <div className="absolute top-1/2 transform -translate-y-1/2 left-[-35px] lg:left-[-50px]">
                        <Icon
                          iconName="arrowLeft"
                          styles="bg-[#F8F8F8] fill-black hover:bg-black hover:fill-white rounded-full p-1 lg:p-2 cursor-pointer prev"
                          width={isMobile ? 26 : 40}
                          height={isMobile ? 26 : 40}
                          onClick={prevSlide}
                        />
                      </div>
                    )}
                    {hoveredSlider && (
                      <div className="absolute top-1/2 transform -translate-y-1/2 right-[-35px] lg:right-[-50px]">
                        <Icon
                          iconName="arrow-right"
                          styles="bg-[#F8F8F8] fill-black hover:bg-black hover:fill-white rounded-full p-1 lg:p-2 cursor-pointer next"
                          width={isMobile ? 26 : 40}
                          height={isMobile ? 26 : 40}
                          onClick={nextSlide}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <ProgramList
                    sectionName="Newest Public Programs"
                    programs={newestProgramPublic?.data?.programs}
                    loading={loadingNewestProgramPublic}
                  />
                )}
              </div>
            </div>
          )}
      </div>
    </>
  )
}
FeaturedBlog.layout = "blog"

export default FeaturedBlog
